import React, { memo, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import Integer from 'components/ForceForm/types/Integer';
import List from 'components/ForceForm/types/List';
import Number from 'components/ForceForm/types/Number';
import Text from 'components/ForceForm/types/Text';
import Date from 'components/ForceForm/types/Date';

import { searchValues } from 'lib/services/valueLists';

import { useWeb5Context } from 'lib/Web5Context';
import ConditionsContext from './ConditionsContext';

const FieldValue = ({
    isReadOnly,
    type,
    className,
    options,
    selectedField,
    table = null,
    onChange = () => null,
    value = null,
}) => {
    const { t, language } = useWeb5Context();
    const context = useContext(ConditionsContext);
    const fieldReadOnly = useMemo(
        () => isReadOnly || context.isReadOnly,
        [context.isReadOnly, isReadOnly],
    );

    const boolOptions = useMemo(
        () => [
            {
                label: t('cfm_label_yes'),
                value: true,
            },
            {
                label: t('cfm_label_no'),
                value: false,
            },
        ],
        [t],
    );

    const handleChange = useCallback(
        (e) => {
            let aux = e?.target?.value || e;
            onChange(aux);
        },
        [onChange],
    );

    const properties = useMemo(
        () => ({
            value,
            name: 'value',
            onChange: handleChange,
            isReadOnly: fieldReadOnly,
            className,
            isFullWidth: true,
        }),
        [fieldReadOnly, handleChange, value, className],
    );

    const onSearchValues = useCallback(
        (text = 'a') => {
            return searchValues({ entity: table, text, lang: language })
                .then(({ data }) => {
                    return data.map((item) => ({
                        label: item.description,
                        value: item.id,
                    }));
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        [language, table],
    );

    const dateProperties = useMemo(
        () => ({
            value: value === null ? undefined : value,
            name: 'value',
            onChange: handleChange,
            isReadOnly: fieldReadOnly,
            className,
            isFullWidth: true,
        }),
        [fieldReadOnly, handleChange, value, className],
    );

    switch (type) {
        case 'bit':
            return <List {...properties} value={properties.value} options={boolOptions} />;
        case 'multivalue':
        case 'list':
            if (properties.value === '') properties.value = null;
            const listProps = options?.listOptions
                ? {
                      table,
                      customGetList: options?.listOptions?.customGetList,
                      idFieldTable: selectedField?.value,
                      selectedField: selectedField?.value,
                      valueObjectNeeded: table,
                  }
                : {
                      loadOptions: onSearchValues,
                      isFuzzy: true,
                      valueObjectNeeded: table,
                      overrides: {
                          'react-select': {
                              defaultOptions: true,
                              openMenuOnClick: true,
                          },
                      },
                  };
            return (
                <List
                    {...properties}
                    placeholder={t('cfm_placeholder_search')}
                    labelMode="horizontal"
                    {...listProps}
                />
            );
        case 'currency':
        case 'decimal':
        case 'percentage':
            return <Number {...properties} placeholder={t('cfm_placeholder_decimal')} />;
        case 'int':
            return <Integer {...properties} placeholder={t('cfm_placeholder_integer')} />;
        case 'datetime':
            return <Date type="datetime" {...dateProperties} />;
        case 'text':
        default:
            if (typeof properties.value !== 'boolean' && typeof properties.value !== 'number') {
                properties.value = properties.value ? properties.value.toString() : '';
            }
            return <Text {...properties} placeholder={t('cfm_placeholder_type_value')} />;
    }
};

FieldValue.propTypes = {
    table: PropTypes.string,
    onChange: PropTypes.func,
    isReadOnly: PropTypes.bool,
    type: PropTypes.oneOf([
        'bit',
        'text',
        'datetime',
        'decimal',
        'int',
        'list',
        'multivalue',
        'currency',
        'percentage',
    ]).isRequired,
    value: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.any),
        PropTypes.bool,
        PropTypes.object,
    ]),
};

export default memo(FieldValue);
